import { msg } from "@lingui/core/macro";
import { Price } from "@silversea-ssc/ui-components-react";
import { fareByPriority, fareCodeLabelForPrice } from "src/utils/fares";
import { formatPrice, getWaitlistLabel } from "src/utils/formatPrice";
import { Currency } from "src/utils/localization/currency";
import { match, P } from "ts-pattern";
import { formatI18nCurrency } from "src/utils/localization/formatCurrency";
import { isPresent } from "src/utils/checks";
import { AvailableWebPrice } from "@silversea-ssc/dotcom-api-types/Prices";
import {
  CruisePriceAndOffersV2Response,
  SuitePriceV2
} from "@silversea-ssc/dotcom-api-types/CruisePriceAndOffersV2";
import { EnhancedAvailablePriceWithFareCode } from "src/ssc-api/prices/types";
import {
  LinguiT,
  translationOrString
} from "src/utils/localization/lingui-helpers";
import { priceForSuite } from "src/utils/suites";

type PriceFromLabelFormat = "with-from" | "without-from" | "only-from" | "none";

// TODO: check if all the formats are used
export const transformPriceFrom = (
  t: LinguiT,
  priceFrom: AvailableWebPrice | undefined,
  currency: Currency | undefined,
  showOriginalDiscountedPrice: boolean,
  labelFormat: PriceFromLabelFormat
): Price | undefined => {
  if (!isPresent(priceFrom)) return undefined;

  return match<[AvailableWebPrice, boolean], Price | undefined>([
    priceFrom,
    showOriginalDiscountedPrice
  ])
    .with([P.nullish, P.any], () => undefined)
    .with([{ type: "waitlist" }, P.boolean], () => ({
      type: "waitlist",
      fareLabel: translationOrString(
        t,
        transformLabelFormat(priceFrom, labelFormat)
      ),
      waitlistLabel: getWaitlistLabel()
    }))
    .with([{ type: "discounted" }, true], ([priceFrom]) => ({
      type: "discounted",
      fareLabel: translationOrString(
        t,
        transformLabelFormat(priceFrom, labelFormat)
      ),
      formattedAmount: formatI18nCurrency(priceFrom.amount, currency) || " ",
      formattedOriginalAmount:
        formatI18nCurrency(priceFrom.originalAmount, currency) || " "
    }))
    .with(
      [{ type: "price" }, P.boolean],
      [{ type: "discounted" }, false],
      ([priceFrom]) => ({
        type: "price",
        fareLabel: translationOrString(
          t,
          transformLabelFormat(priceFrom, labelFormat)
        ),
        formattedAmount: formatPrice(priceFrom, currency) || " "
      })
    )
    .exhaustive();
};

const transformLabelFormat = (
  price: EnhancedAvailablePriceWithFareCode,
  labelFormat: PriceFromLabelFormat
) =>
  match(labelFormat)
    .with("none", () => "")
    .with("only-from", () => fromPiceLabel())
    .with("with-from", "without-from", (format) =>
      fareCodeLabelForPrice(price, format)
    )
    .exhaustive();

export const suitePricestoPrices = (
  t: LinguiT,
  suiteCategotyCodes: string[],
  price: readonly SuitePriceV2[],
  currency: Currency | undefined,
  showOriginalDiscountedPrice = false,
  labelFormat?: PriceFromLabelFormat
):
  | { prices: Price[]; suiteCategory: string; originalSuitePrice: SuitePriceV2 }
  | undefined => {
  const suitePrices = priceForSuite(price, suiteCategotyCodes);
  if (!isPresent(suitePrices) || !suitePrices.prices) return undefined;
  const transformedPrces = [...suitePrices.prices]
    .sort((a, b) => fareByPriority(a.fare, b.fare))
    .map((p) =>
      transformPriceFrom(
        t,
        p.price,
        currency,
        showOriginalDiscountedPrice,
        labelFormat || "without-from"
      )
    )
    .filter(isPresent);

  return {
    prices: transformedPrces,
    suiteCategory: suitePrices.suiteCategory,
    originalSuitePrice: suitePrices
  };
};

export const cruisePriceToPrices = (
  t: LinguiT,
  prices: CruisePriceAndOffersV2Response | undefined,
  currency: Currency | undefined
) => [transformPriceFrom(t, prices?.priceFrom, currency, true, "none")];

export const fromPiceLabel = () => msg({ id: "from-price" });
export const withLowestFareAvailableLabel = () =>
  msg`with lowest fare available`;
export const perGuestLabel = () => msg`Per guest`;
export const isPriceWaitlist = <T extends { type: AvailableWebPrice["type"] }>(
  priceFrom: T | undefined
) => priceFrom?.type == "waitlist";
